/*
* Store universalStats - Управление состоянием универсальной статистики
*
* Основные функции:
* - Загрузка агрегированных статистических данных по типам 'adverse' и 'ticket'
* - Динамическое управление метаданными таблицы (заголовки, фильтры)
* - Реализация многоуровневой фильтрации с поддержкой серверного поиска и бесконечной подгрузки вариантов
* - Синхронизация параметров запроса (сортировка, пагинация, агрегация) с API
*/

import axios from 'axios'
import { defineStore } from 'pinia'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { BASE_URL } from '@/environment/environment'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'

import type { IUniversalStatsStoreState } from '@/modules/universalStats/types/IUniversalStatsStoreState'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

export const useUniversalStatsStore = defineStore('universalStats', {
  state: (): IUniversalStatsStoreState => ({
    // Ссылка на следующую страницу основной таблицы для курсорной пагинации
    nextCursor: null,

    // Общее количество элементов статистики на сервере
    totalStatsItemsCount: 0,

    // Текущее количество строк на одну страницу
    itemsPerPage: 25,

    // Список загруженных элементов статистики
    statsItems: [],

    // Динамические заголовки колонок, приходящие от API
    currentTableHeaders: [],

    // Текущий выбранный тип агрегации данных (например, по координатору)
    currentAggregateType: 'coordinator',

    // Текущая строка сортировки
    currentSortString: '',

    // Объект активных фильтров: ключ — название поля, значение — массив выбранных ID
    activeFilters: {},

    // Кеш доступных вариантов для выпадающих списков фильтров
    filterChoices: {},

    // Кеш результатов поиска внутри конкретного фильтра
    searchChoices: {},

    // Ссылки на следующие страницы для каждого отдельного фильтра (для бесконечного скролла в дереве)
    filterCursors: {},

    // Состояния загрузки для каждого отдельного фильтра
    filterLoading: {},

    // Хранение страгеии выбора
    filterStrategies: {},

    // Временные интервалы
    dateAfter: null,
    dateBefore: null,

    // Конфигурация доступных вкладок агрегации для разных модулей
    aggregateItems: {
      adverse: [
        { name: 'Координаторы', value: 'coordinator' },
        { name: 'Отделения', value: 'department' },
        { name: 'Вид НС', value: 'type' },
      ],
      ticket: [
        { name: 'Координаторы', value: 'coordinator' },
        { name: 'Отделения', value: 'department' },
        { name: 'Категория', value: 'category' },
      ],
    },
  }),

  actions: {
    /**
     * Загрузка основных статистических данных
     * @param statsType - Тип статистики: нежелательные явления или тикеты
     * @param cursor - Указатель для загрузки следующей страницы
     * @param itemsPerPage - Лимит записей на страницу
     * @param sort - Параметр сортировки
     */
    async fetchStats(
      statsType: 'adverse' | 'ticket',
      cursor?: string,
      itemsPerPage?: number,
      sort?: string,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()

      // Активация глобального индикатора загрузки
      feedbackStore.isGlobalLoading = true

      // Определение эндпоинта на основе выбранного модуля
      const endpoint = statsType === 'adverse'
        ? onboardingApiUrl('adverse_event', '/adverse/adverse-event/statistic/')
        : onboardingApiUrl('ticket', '/ticket/ticket/statistic/')

      try {
        // Базовые параметры запроса
        const params: Record<string, any> = {
          aggregate: this.currentAggregateType,
        }

        // Настройка параметров пагинации и сортировки
        if (itemsPerPage) {
          params.page_size = itemsPerPage
          this.itemsPerPage = itemsPerPage
        }
        if (cursor) params.cursor = cursor
        if (sort) params.sort = sort

        // Добавление фильтров по дате
        if (this.dateAfter) params.created_after = this.dateAfter
        if (this.dateBefore) params.created_before = this.dateBefore

        // Преобразование активных фильтров в формат Query Params (запятые между ID)
        Object.keys(this.activeFilters).forEach(key => {
          if (this.activeFilters[key].length > 0) {
            params[key] = this.activeFilters[key].join(',')
          }
        })

        const response = await axios.get(endpoint, { params })
        const results = response.data.results

        // Обновление метаданных пагинации и структуры таблицы
        this.nextCursor = response.data.next
        this.totalStatsItemsCount = response.data.count
        this.currentTableHeaders = response.data.headers ?? []

        // Если есть курсор — дополняем массив (пагинация), иначе заменяем (новый запрос)
        if (cursor) {
          this.statsItems = [...this.statsItems, ...results]
        } else {
          this.statsItems = results
        }
      } catch (error) {
        console.error(`❌ Ошибка загрузки статистики ${statsType}: ${String(error)}`)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка вариантов для выпадающих списков фильтрации (choices)
     * UPD: Добавлена отправка активных фильтров (контекст) и замена search на search_field
     */
    async fetchFilterChoices(
      fieldKey: string,
      searchQuery: string,
      cursor?: string,
      module?: IUniversalTreeSelectModule,
    ): Promise<void> {
      // Установка локального индикатора загрузки для конкретной колонки
      this.filterLoading[fieldKey] = true

      const endpoint = module === 'adverse'
        ? '/api/adverse/adverse-event/statistic/filter-choices/'
        : '/api/ticket/ticket/statistic/filter-choices/'

      try {
        const params: Record<string, any> = {
          field: fieldKey,
        }

        // 1. Изменение параметра поиска
        if (searchQuery) params.search_field = searchQuery

        if (cursor) params.cursor = cursor

        // 2. Подмешивание активных фильтров из activeFilters
        // В универсальной статистике все динамические фильтры лежат в activeFilters (Record<string, string[]>)
        Object.keys(this.activeFilters).forEach(key => {
          // Исключаем текущее поле и пустые фильтры
          if (key !== fieldKey && this.activeFilters[key].length > 0) {
            params[key] = this.activeFilters[key].join(',')
          }
        })

        // 3. Подмешивание дат
        if (fieldKey !== 'created_after' && this.dateAfter) {
          params.created_after = this.dateAfter // В этом сторе даты уже хранятся в API формате
        }
        if (fieldKey !== 'created_before' && this.dateBefore) {
          params.created_before = this.dateBefore
        }

        const response = await axios.get(`${BASE_URL}${endpoint}`, { params })

        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (searchQuery) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (error) {
        console.error(`❌ Ошибка фильтра ${fieldKey}: ${String(error)}`)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },
  },
})
